import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  WORKOUT_CATEGORIES,
  WORKOUT_DIFFICULTIES,
  WORKOUT_DURATIONS,
  WORKOUT_EXERCISE_COUNTS,
  WORKOUT_SORT_OPTIONS,
  type WorkoutLibraryFilterState,
} from "@/lib/workout-library-filters";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: WorkoutLibraryFilterState;
  onFiltersChange: (filters: WorkoutLibraryFilterState) => void;
  muscleOptions: string[];
  categoryOptions: string[];
  resultCount: number;
};

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="font-medium mb-2 text-white">{title}</h3>
      {children}
    </div>
  );
}

function SingleSelectBadges({
  options,
  selected,
  onSelect,
}: {
  options: readonly string[];
  selected: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => (
        <Badge
          key={option}
          variant="outline"
          className={`cursor-pointer border-primary/30 ${
            selected === option ? "bg-primary text-black border-primary" : "text-white"
          }`}
          onClick={() => onSelect(option)}
        >
          {option}
        </Badge>
      ))}
    </div>
  );
}

function MultiSelectBadges({
  options,
  selected,
  onToggle,
}: {
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  if (options.length === 0) {
    return <p className="text-sm text-gray-light">No muscle groups available yet.</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const isSelected = selected.includes(option);
        return (
          <Badge
            key={option}
            variant="outline"
            className={`cursor-pointer border-primary/30 ${
              isSelected ? "bg-primary text-black border-primary" : "text-white"
            }`}
            onClick={() => onToggle(option)}
          >
            {option}
          </Badge>
        );
      })}
    </div>
  );
}

export default function WorkoutLibraryFiltersDrawer({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  muscleOptions,
  categoryOptions,
  resultCount,
}: Props) {
  const categories =
    categoryOptions.length > 0
      ? ["All", ...categoryOptions]
      : (WORKOUT_CATEGORIES as readonly string[]);

  const update = (patch: Partial<WorkoutLibraryFilterState>) => {
    onFiltersChange({ ...filters, ...patch });
  };

  const toggleMuscle = (muscle: string) => {
    const next = filters.selectedMuscleGroups.includes(muscle)
      ? filters.selectedMuscleGroups.filter((m) => m !== muscle)
      : [...filters.selectedMuscleGroups, muscle];
    update({ selectedMuscleGroups: next });
  };

  const clearAll = () => {
    onFiltersChange({
      selectedCategory: "All",
      selectedDifficulty: "All",
      selectedDuration: "All",
      selectedMuscleGroups: [],
      selectedExerciseCount: "All",
      sortBy: "default",
    });
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-dark-card text-white border-primary/20">
        <DrawerHeader className="text-left">
          <DrawerTitle>Filter Workouts</DrawerTitle>
          <DrawerDescription className="text-gray-light">
            Narrow the library by difficulty, duration, muscles, and more.
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 space-y-5 overflow-y-auto max-h-[60vh] pb-2">
          <FilterSection title="Sort by">
            <SingleSelectBadges
              options={WORKOUT_SORT_OPTIONS.map((o) => o.label)}
              selected={
                WORKOUT_SORT_OPTIONS.find((o) => o.value === filters.sortBy)?.label ?? "Default"
              }
              onSelect={(label) => {
                const match = WORKOUT_SORT_OPTIONS.find((o) => o.label === label);
                if (match) update({ sortBy: match.value });
              }}
            />
          </FilterSection>

          {categoryOptions.length > 0 && (
            <FilterSection title="Category">
              <SingleSelectBadges
                options={categories}
                selected={filters.selectedCategory}
                onSelect={(value) => update({ selectedCategory: value })}
              />
            </FilterSection>
          )}

          <FilterSection title="Difficulty">
            <SingleSelectBadges
              options={WORKOUT_DIFFICULTIES}
              selected={filters.selectedDifficulty}
              onSelect={(value) => update({ selectedDifficulty: value })}
            />
          </FilterSection>

          <FilterSection title="Duration">
            <SingleSelectBadges
              options={WORKOUT_DURATIONS}
              selected={filters.selectedDuration}
              onSelect={(value) => update({ selectedDuration: value })}
            />
          </FilterSection>

          <FilterSection title="Muscle groups">
            <MultiSelectBadges
              options={muscleOptions}
              selected={filters.selectedMuscleGroups}
              onToggle={toggleMuscle}
            />
          </FilterSection>

          <FilterSection title="Exercise count">
            <SingleSelectBadges
              options={WORKOUT_EXERCISE_COUNTS}
              selected={filters.selectedExerciseCount}
              onSelect={(value) => update({ selectedExerciseCount: value })}
            />
          </FilterSection>
        </div>

        <DrawerFooter className="border-t border-primary/10 pt-4">
          <Button
            variant="outline"
            className="border-primary/30 text-white hover:bg-white/10"
            onClick={clearAll}
          >
            Clear all
          </Button>
          <Button
            className="gradient-primary text-black hover:opacity-90"
            onClick={() => onOpenChange(false)}
          >
            Show {resultCount} workout{resultCount === 1 ? "" : "s"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
